import React from 'react';
import moment from 'moment';
import { FolderAddOutlined, FolderOpenOutlined } from '@ant-design/icons';
import {
  AssignFolderButton,
  FeedAuthor,
  FeedAuthorRow,
  FeedBody,
  FeedBodyTranslation,
  FeedCard,
  FeedCardContent,
  FeedCardHeader,
  FeedMeta,
  FeedTime,
  FolderBadge,
  MobileHideLabel,
  FolderPickerBackdrop,
  FolderPickerEmpty,
  FolderPickerItem,
  FolderPickerRemoveItem,
  FolderPickerWrap,
} from './index.styled';
import { CommunityMessage, Folder } from './types';
import MessageComments from './MessageComments';
import { Avatar } from 'antd';
import { isRtlText } from 'utils/textDirection';

type Props = {
  message: CommunityMessage;
  folders: Folder[];
  assignedFolderId: string | undefined;
  isPickerOpen: boolean;
  onTogglePicker: () => void;
  onClosePicker: () => void;
  onAssignFolder: (folderId: string | null) => void;
  isLoggedIn: boolean;
  currentUserId?: string;
  onCommentsCountChange?: (messageId: string, count: number) => void;
  isTranslateEnabled?: boolean;
};

export default function MessageCard({
  message,
  folders,
  assignedFolderId,
  isPickerOpen,
  onTogglePicker,
  onClosePicker,
  onAssignFolder,
  isLoggedIn,
  currentUserId,
  onCommentsCountChange,
  isTranslateEnabled = true,
}: Props) {
  const originalContent = message.originalContent || message.content;
  const translatedContent = message.translated_content;
  const displayContent = message.content;
  const isOriginalRtl = isRtlText(originalContent);
  const showBilingual =
    isTranslateEnabled &&
    Boolean(translatedContent) &&
    translatedContent !== originalContent &&
    isOriginalRtl;

  return (
    <FeedCard>
      <Avatar style={{ background: 'grey', verticalAlign: 'middle' }} size="large">
        {(message.senderName.trim()[0] ?? 'T').toUpperCase()}
      </Avatar>

      <FeedCardContent>
        <FeedCardHeader>
          <FeedMeta>
            <FeedAuthorRow>
              <FeedAuthor>{message.senderName}</FeedAuthor>
              <FeedTime>
                {message.date
                  ? (() => {
                    const diff = moment().diff(moment(message.date), 'minutes');
                    if (diff < 60) {
                      return `${diff}m ago`;
                    } else if (diff < 1440) { // 24 * 60 = 1440 minutes
                      const hours = Math.floor(diff / 60);
                      return `${hours}h ago`;
                    } else {
                      return moment(message.date).format('MMM D');
                    }
                  })()
                  : 'Unknown time'}
              </FeedTime>
            </FeedAuthorRow>
          </FeedMeta>
          {assignedFolderId ? (
            <FolderBadge type="button" onClick={onTogglePicker}>
              <FolderOpenOutlined />
              {folders.find((f) => f.id === assignedFolderId)?.name ?? 'Folder'}
            </FolderBadge>
          ) : (
            <AssignFolderButton type="button" onClick={onTogglePicker}>
              <FolderAddOutlined /> <MobileHideLabel>Add to folder</MobileHideLabel>
            </AssignFolderButton>
          )}

          {isPickerOpen && (
            <>
              <FolderPickerBackdrop onClick={onClosePicker} />
              <FolderPickerWrap>
                {folders.map((folder) => (
                  <FolderPickerItem
                    key={folder.id}
                    type="button"
                    $active={assignedFolderId === folder.id}
                    onClick={() => onAssignFolder(folder.id)}
                  >
                    {folder.name}
                  </FolderPickerItem>
                ))}
                {assignedFolderId && (
                  <FolderPickerRemoveItem type="button" onClick={() => onAssignFolder(null)}>
                    Remove from folder
                  </FolderPickerRemoveItem>
                )}
                {folders.length === 0 && (
                  <FolderPickerEmpty>No folders yet. Create one in the sidebar.</FolderPickerEmpty>
                )}
              </FolderPickerWrap>
            </>
          )}
        </FeedCardHeader>

        {showBilingual ? (
          <>
            <FeedBody $rtl>{originalContent}</FeedBody>
            <FeedBodyTranslation>{translatedContent}</FeedBodyTranslation>
          </>
        ) : (
          <FeedBody $rtl={isRtlText(displayContent)}>{displayContent}</FeedBody>
        )}

        <MessageComments
          messageId={message.id}
          commentsCount={message.commentsCount}
          isLoggedIn={isLoggedIn}
          currentUserId={currentUserId}
          onCommentsCountChange={(count) => onCommentsCountChange?.(message.id, count)}
        />
      </FeedCardContent>
    </FeedCard>
  );
}
